import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import "@/App.css";

// Pages
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import CreateSong from './pages/CreateSong';
import CreateSongFromImage from './pages/CreateSongFromImage';
import CreateVideo from './pages/CreateVideo';
import CreateVideoBeta from './pages/CreateVideoBeta';
import CreateCover from './pages/CreateCover';
import CreateGift from './pages/CreateGift';
import GiftView from './pages/GiftView';
import MyGifts from './pages/MyGifts';
import SongDetail from './pages/SongDetail';
import VideoDetail from './pages/VideoDetail';
import AdminPanel from './pages/AdminPanel';
import PromoCampaignPrint from './pages/PromoCampaignPrint';
import { CoachProvider } from './components/Coach';
import AuthCallback from './pages/AuthCallback';
import AuthPage from './pages/AuthPage';
import Settings from './pages/Settings';
import BootstrapAdmin from './pages/BootstrapAdmin';
import Feedback from './pages/Feedback';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import BirthdaySong from './pages/BirthdaySong';
import PublicPlayer from './pages/PublicPlayer';
import Library from './pages/Library';
import Redeem from './pages/Redeem';
import MyAIClips from './pages/MyAIClips';
// (ChameleonsPreview removed in v2.9.51 — Animation Studio replaces it)
import ErrorBoundary from './components/ErrorBoundary';
import Footer from './components/Footer';
import SongCompletionNotifier from './components/SongCompletionNotifier';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Detect if we're on a custom domain (not emergent preview)
// If so, use relative URLs so cookies work
const isCustomDomain = typeof window !== 'undefined' && 
  !window.location.hostname.includes('emergentagent.com') &&
  !window.location.hostname.includes('emergent.host') &&
  !window.location.hostname.includes('localhost');

export const API = isCustomDomain ? '/api' : `${BACKEND_URL}/api`;

// Use native fetch stored before tracking scripts loaded
const nativeFetch = window.__nativeFetch || window.fetch;

// Token storage key
const TOKEN_KEY = 'wim_session_token';

// Get stored token
export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);

// Store token
export const storeToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
};

// Clear token
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Helper for API calls using native fetch with token fallback
export const apiFetch = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API}${endpoint}`;

  // Detect FormData body — when uploading multipart, the browser MUST
  // set Content-Type itself (it needs to inject the right
  // `multipart/form-data; boundary=...` value). Forcing
  // `application/json` here breaks FastAPI's `Form(...)` parsers and
  // surfaces as a 422 validation error.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

  // Build headers — default to JSON for regular API calls, but never
  // for FormData uploads.
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };
  // If a caller explicitly set Content-Type for FormData, strip it.
  if (isFormData) delete headers['Content-Type'];

  // Add Authorization header if we have a stored token
  const storedToken = getStoredToken();
  if (storedToken) {
    headers['Authorization'] = `Bearer ${storedToken}`;
  }

  const response = await nativeFetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });
  const text = await response.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = text;
  }
  if (!response.ok) {
    // FastAPI returns `detail` as a string for HTTPException, but for
    // validation errors it's an array of `{loc, msg, type}` objects.
    // Stringifying that array naively yields the infamous
    // `[object Object],[object Object]` toast — extract a readable
    // message instead.
    const detail = data?.detail;
    let message;
    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail)) {
      // Pull msg fields off each validation entry and join.
      message = detail
        .map((e) => (e && typeof e === 'object' ? (e.msg || e.message || JSON.stringify(e)) : String(e)))
        .filter(Boolean)
        .join('; ');
    } else if (detail && typeof detail === 'object') {
      message = detail.msg || detail.message || JSON.stringify(detail);
    } else {
      message = 'Request failed';
    }
    const error = new Error(message || 'Request failed');
    error.response = { data, status: response.status };
    throw error;
  }
  return { data, response };
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Retry-on-401 auth check. Cold loads and multi-tab sessions can produce
  // transient 401s (backend warm-up, request races, cookie/Bearer ambiguity).
  // We retry once with a brief delay before giving up — this prevents the
  // recurring "bounced to home" + "0 songs on dashboard" symptoms that users
  // experienced after refreshing or opening a second tab.
  const checkAuth = useCallback(async (isRetry = false) => {
    try {
      const { data } = await apiFetch('/auth/me');
      console.log('Auth check successful, user:', data?.email, 'role:', data?.role);
      setUser(data);
      setLoading(false);
    } catch (error) {
      const status = error.response?.status;
      console.error('Auth check failed:', error, 'status:', status, 'retry:', isRetry);

      if (status === 401) {
        const hasToken = !!getStoredToken();
        // First 401 with a stored token → retry once after a short delay.
        // This catches transient backend races on cold load / multi-tab.
        if (!isRetry && hasToken) {
          console.log('Got transient 401 with stored token — retrying once in 800ms');
          await new Promise(r => setTimeout(r, 800));
          return checkAuth(true);
        }
        // Confirmed unauthenticated — clear stored token (if any) and any
        // existing in-memory user. Only clear an existing session if we
        // truly have no token; otherwise keep current state to avoid
        // bouncing an active user on a transient backend hiccup.
        if (hasToken && isRetry) {
          // Two consecutive 401s with a token → the token really is dead.
          clearToken();
          setUser(null);
        } else if (!hasToken) {
          setUser(null);
        }
        // else: no token but a non-retry path — leave user as is (no session)
      }
      // Non-401 (network/5xx): keep existing user state, do not log out.
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Multi-tab logout sync — if another tab clears the session token,
  // mirror that here so the user doesn't stay "logged in" with a dead token.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TOKEN_KEY && !e.newValue) {
        console.log('Token cleared in another tab — syncing logout');
        setUser(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // bfcache restoration (v2.9.49)
  // ---------------------------------------------------------------
  // Browsers (Chrome, Safari, Firefox) freeze entire React pages in
  // memory on navigation away and restore them as-is when the user
  // hits Back. No component remounts, no `useEffect` re-runs — so
  // any state in flight when the user left (a transient 401, a
  // half-loaded fetch, a "loading…" placeholder) gets resurrected
  // verbatim, producing the "logged out / zero songs after Back"
  // symptom. The fix is to detect bfcache restoration (pageshow with
  // event.persisted === true) and re-run auth, which causes the
  // protected pages to refetch their data once user is rehydrated.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) {
        console.log('Restored from bfcache — re-validating session');
        checkAuth();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [checkAuth]);

  // Heartbeat — pings /auth/me every 60s while the tab is visible
  // and the user is logged in. Backend uses this to stamp
  // `users.last_seen_at`, which powers the admin "Live Users" widget.
  // Skips when the tab is hidden (mobile sleep / background tab) so
  // we don't keep stale users marked online forever.
  useEffect(() => {
    if (!user) return undefined;
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      apiFetch('/auth/me').catch(() => {});
    };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [user]);

  const login = async () => {
    // Dual-mode auth (Pass 1):
    // - Ask the backend which flow is active.
    // - If the backend reports google_oauth_enabled=true (self-hosted with
    //   GOOGLE_CLIENT_ID/SECRET configured), redirect to the backend's
    //   /auth/google/start endpoint.
    // - Otherwise fall back to the Emergent Auth proxy.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const { data: cfg } = await apiFetch('/auth/config');
      if (cfg?.google_oauth_enabled) {
        window.location.href = `${API}/auth/google/start`;
        return;
      }
    } catch (err) {
      console.warn('auth/config lookup failed, falling back to Emergent Auth:', err);
    }
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear stored token
    clearToken();
    setUser(null);
  };

  const refreshUser = async () => {
    // Check if we have user data from login response (fallback if cookies don't work)
    if (window.__loginUser) {
      setUser(window.__loginUser);
      delete window.__loginUser;
      return;
    }
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Re-evaluate token presence on every render so cleared tokens (after a
  // confirmed-dead retry inside checkAuth) flip this guard off and let the
  // bounce-to-home effect run.
  const hasStoredToken = !!getStoredToken();

  useEffect(() => {
    // If user data was passed from AuthCallback, use it
    if (location.state?.user) {
      return;
    }

    // Only bounce to home when we have NO user AND NO stored token to retry
    // with. If a token is still in localStorage, checkAuth is either
    // re-validating it or about to clear it — wait that out instead of
    // booting the user to "/".
    if (!loading && !user && !hasStoredToken) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate, location.state, hasStoredToken]);

  // Show loading spinner while:
  //   (a) AuthProvider is still bootstrapping (loading=true), OR
  //   (b) We have a stored token but the user object hasn't materialised yet
  //       (mid-retry or freshly mounted protected page).
  if (loading || (!user && hasStoredToken)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            <div className="audio-bar" style={{ animationDelay: '0s' }}></div>
            <div className="audio-bar" style={{ animationDelay: '0.1s' }}></div>
            <div className="audio-bar" style={{ animationDelay: '0.2s' }}></div>
            <div className="audio-bar" style={{ animationDelay: '0.3s' }}></div>
            <div className="audio-bar" style={{ animationDelay: '0.4s' }}></div>
          </div>
          <span className="text-white">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id BEFORE rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/register" element={<AuthPage />} />
      <Route path="/gift/:giftId" element={<GiftView />} />
      <Route path="/setup-admin" element={<BootstrapAdmin />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/create-song" element={<ProtectedRoute><CreateSong /></ProtectedRoute>} />
      <Route path="/create-from-image" element={<ProtectedRoute><CreateSongFromImage /></ProtectedRoute>} />
      <Route path="/create-video" element={<ProtectedRoute><CreateVideo /></ProtectedRoute>} />
      <Route path="/slideshow-beta" element={<ProtectedRoute><CreateVideoBeta /></ProtectedRoute>} />
      {/* Legacy bookmark redirect — old /slideshow-legacy URL now routes to production /create-video */}
      <Route path="/slideshow-legacy" element={<Navigate to="/create-video" replace />} />
      <Route path="/create-cover" element={<ProtectedRoute><CreateCover /></ProtectedRoute>} />
      <Route path="/create-gift" element={<ProtectedRoute><CreateGift /></ProtectedRoute>} />
      <Route path="/my-gifts" element={<ProtectedRoute><MyGifts /></ProtectedRoute>} />
      <Route path="/song/:songId" element={<ProtectedRoute><SongDetail /></ProtectedRoute>} />
      <Route path="/songs/:songId" element={<ProtectedRoute><SongDetail /></ProtectedRoute>} />
      <Route path="/video/:videoId" element={<ProtectedRoute><VideoDetail /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/admin/promo/print/:campaignId" element={<ProtectedRoute><PromoCampaignPrint /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/my-ai-clips" element={<ProtectedRoute><MyAIClips /></ProtectedRoute>} />
      {/* /preview/chameleons route removed in v2.9.51 with the legacy
          ChameleonStage. Animation Studio (admin) replaces it. */}
      <Route path="/redeem" element={<ProtectedRoute><Redeem /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/birthday" element={<BirthdaySong />} />
      {/* Public player routes - no auth required */}
      <Route path="/play/:type/:id" element={<PublicPlayer />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CoachProvider>
          <div className="App min-h-screen bg-background flex flex-col">
            <div className="noise-overlay"></div>
            <div className="flex-1">
              <ErrorBoundary>
                <AppRouter />
              </ErrorBoundary>
            </div>
            <SongCompletionNotifier />
            <Footer />
            <Toaster
              position="bottom-center"
              theme="dark"
              toastOptions={{
                style: {
                  background: '#121212',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontSize: '16px',
                  padding: '16px 24px'
                }
              }}
            />
          </div>
        </CoachProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
